import type { FC } from 'react';
import { useMemo } from 'react';

import classNames from 'classnames';
import round from 'lodash/round';

import styles from './Slide.module.css';

import { ButtonBase, type ButtonBaseProps } from '../ButtonBase';
import * as animations from './animations';
import { getScale } from './getScale';

export interface SlideProps extends ButtonBaseProps {
  /** Whether to toggle the wide view */
  isWide: boolean;
  /** The index of the slide */
  index: number;
  /** Whether to toggle the debug mode for this component */
  isDebug: boolean;
  /** The amount the slide is intersecting the parent element */
  intersectionRatio: number;
  /** Whether this is the active slide */
  active: boolean;
}

export const Slide: FC<SlideProps> = ({
  children,
  className,
  isWide = false,
  index,
  intersectionRatio,
  isDebug = false,
  active,
  ...props
}) => {
  const responsiveAnimation = !isWide ? animations.notWide : animations.wide;

  const style = useMemo(() => {
    const { minScale, maxScale, curve } = responsiveAnimation;

    const progress = curve(intersectionRatio);
    const scale = getScale(progress, minScale, maxScale);

    return { transform: `scale(${scale})` };
  }, [intersectionRatio, responsiveAnimation]);

  const showDebug = index === 2 && isDebug;
  const activeStyle = active ? styles.active : styles.inactive;

  return (
    <li
      className={classNames(styles.slide, className, {
        [styles['is-wide']]: isWide,
        [styles['is-not-wide']]: !isWide,
      })}
    >
      <div
        className={classNames(
          styles['animation-reference'],
          showDebug && styles['animation-debug']
        )}
      >
        {showDebug &&
          `Animation reference intersection ${round(intersectionRatio, 2)}`}
      </div>
      <ButtonBase
        className={classNames(styles.button, activeStyle)}
        style={style}
        {...props}
      >
        {children}
      </ButtonBase>
    </li>
  );
};
